import { Button } from '@/components/ui/button';
import { Download, X } from 'lucide-react';
import { SafeImage } from '@/components/ui/safe-image';
import { MediaItem } from '@property/shared';

interface ImageViewerProps {
  media: MediaItem | null;
  onClose: () => void;
}

export function ImageViewer({ media, onClose }: ImageViewerProps) {
  if (!media) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8 bg-background/95 backdrop-blur-xl animate-in fade-in duration-300"
      onClick={onClose}
    >
      <div className="absolute top-6 right-6 z-10 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-12 w-12 rounded-full bg-muted/50 hover:bg-muted text-foreground transition-all duration-300 shadow-xl"
          onClick={() => {
            if (!media) return;
            const link = document.createElement('a');
            link.href = media.url;
            const urlExt = media.url.split('.').pop()?.split('?')[0];
            const ext = urlExt ?? (media.type === 'video' ? 'mp4' : 'jpg');
            link.download = `${(media.label || 'media').replace(/\s+/g, '-').toLowerCase()}.${ext}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
        >
          <Download className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-12 w-12 rounded-full bg-muted/50 hover:bg-muted text-foreground transition-all duration-300 shadow-xl"
          onClick={onClose}
        >
          <X className="h-6 w-6" />
        </Button>
      </div>

      <div
        className="relative w-full max-w-7xl h-full flex flex-col items-center justify-center gap-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full h-[85vh] group/image">
          {media.type === 'video' ? (
            <video
              src={media.url || ''}
              className="w-full h-full object-contain drop-shadow-2xl"
              controls
              autoPlay
            />
          ) : (
            <SafeImage
              src={media.url || ''}
              alt={media.label || ''}
              className="w-full h-full object-contain drop-shadow-2xl transition-transform duration-500 hover:scale-[1.02]"
            />
          )}
        </div>

        <div className="w-full max-w-2xl text-center space-y-2 px-4">
          <h3 className="text-2xl font-black tracking-tight text-foreground">{media.label}</h3>
        </div>
      </div>
    </div>
  );
}
